import Typography from '@mui/material/Typography';
import { useTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { SelectTagsContextMenu } from 'shared/components/connected/select-tags-context-menu';
import { IconButton, Sizes } from 'shared/components/ui/icon-button';
import { Toggle } from 'shared/components/ui/toggle';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { ID } from 'shared/types/id';

import { useExistingFilterTags } from '../../hooks/use-existing-filter-tags';
import * as Styled from './side-panel.style';

export const AllTasksFilters = () => {
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const { t } = useTranslation();
  const tags = useTags();

  const existingFilteredTags = useExistingFilterTags();

  const tagContainerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const toggleTags = () => (menuOpen ? closeMenu() : openMenu());

  const updateUnplannedGoals = () =>
    updateUserSettings({
      upcoming: {
        allTasks: {
          hideUnplannedGoals:
            !user?.settings?.upcoming?.allTasks?.hideUnplannedGoals,
        },
      },
    });
  const updateEmptyLifeAreas = () =>
    updateUserSettings({
      upcoming: {
        allTasks: {
          hideEmptyLifeAreas:
            !user?.settings?.upcoming?.allTasks?.hideEmptyLifeAreas,
        },
      },
    });

  const updateHabits = () =>
    updateUserSettings({
      upcoming: {
        allTasks: {
          hideHabits: !user?.settings?.upcoming?.allTasks?.hideHabits,
        },
      },
    });

  const updateTags = (tagId: ID) => {
    const newTags = existingFilteredTags.filter((id) => id !== tagId);
    if (newTags.length === existingFilteredTags.length) {
      newTags.push(tagId);
    }

    updateUserSettings({
      upcoming: {
        allTasks: {
          filterByTags: newTags,
        },
      },
    });
  };

  return (
    <>
      {!!tags.length && (
        <>
          <Styled.Filter ref={tagContainerRef} onClick={toggleTags}>
            <Typography variant="buttonSmall" component="label">
              {t('pages.upcoming.filters.tags.label')}
            </Typography>
            {!!existingFilteredTags.length && (
              <Styled.Badge>{existingFilteredTags.length}</Styled.Badge>
            )}
            <IconButton
              icon={ChevronLeft}
              rotation={menuOpen ? 90 : 270}
              size={Sizes.Small}
              as="span"
            />
          </Styled.Filter>

          {menuOpen && (
            <SelectTagsContextMenu
              onTag={updateTags}
              activeTags={existingFilteredTags}
              onClose={closeMenu}
              referenceElement={tagContainerRef}
            />
          )}
        </>
      )}

      <Styled.Filter>
        <Typography variant="buttonSmall" component="label" htmlFor="habits">
          {t('pages.upcoming.filters.habits.label')}
        </Typography>
        <Toggle
          value="habits"
          id="habits"
          onChange={updateHabits}
          checked={!user?.settings?.upcoming?.allTasks?.hideHabits}
          isSmall
        />
      </Styled.Filter>

      <Styled.Filter>
        <Typography
          variant="buttonSmall"
          component="label"
          htmlFor="unplanned-goals"
        >
          {t('pages.upcoming.filters.unplanned-goals.label')}
        </Typography>
        <Toggle
          value="unplanned-goals"
          id="unplanned-goals"
          onChange={updateUnplannedGoals}
          checked={!user?.settings?.upcoming?.allTasks?.hideUnplannedGoals}
          isSmall
        />
      </Styled.Filter>

      <Styled.Filter>
        <Typography
          variant="buttonSmall"
          component="label"
          htmlFor="empty-life-areas"
        >
          {t('pages.upcoming.filters.empty-life-areas.label')}
        </Typography>
        <Toggle
          value="empty-life-areas"
          id="empty-life-areas"
          onChange={updateEmptyLifeAreas}
          checked={!user?.settings?.upcoming?.allTasks?.hideEmptyLifeAreas}
          isSmall
        />
      </Styled.Filter>
    </>
  );
};
